import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '../context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import InfoTooltip from '@/components/InfoTooltip.jsx';

const buildPlayerInvitePath = (game, playerId) => {
  if (!game?.session?.roomId || !game?.session?.inviteToken) {
    return '';
  }
  return `/gamepage/${game.session.roomId}?token=${game.session.inviteToken}&player=${playerId}`;
};

const GameActionTooltip = ({ label, children }) => (
  <InfoTooltip content={label} className="games-action-tooltip"
    placement="top" autoFlip fallbackPlacements={['bottom']}>
    {children}
  </InfoTooltip>
);

const GamesListPage = ({ type }) => {
  const { t, i18n } = useTranslation('game');
  const { token } = useAuth();
  const recordingsLabel = i18n.language?.startsWith('en') ? 'Recordings' : 'Записи';
  const { getLocalizedPath } = useLanguageNavigation();
  const statusTabs = useMemo(
    () => [
      { value: 'active', label: t('list.statusTabs.active') },
      { value: 'planned', label: t('list.statusTabs.planned') },
      { value: 'finished', label: t('list.statusTabs.finished') },
    ],
    [t],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'future') {
      return 'planned';
    }
    if (tab === 'active' || tab === 'planned' || tab === 'finished') {
      return tab;
    }
    return 'active';
  });
  const trashLabel = status === 'finished' ? t('list.actions.hide') : t('list.actions.finish');
  const [page, setPage] = useState(() => {
    const parsed = Number.parseInt(searchParams.get('page') || '1', 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const parsed = Number.parseInt(searchParams.get('pageSize') || '20', 10);
    if (!Number.isInteger(parsed)) {
      return 20;
    }
    if ([20, 50, 100].includes(parsed)) {
      return parsed;
    }
    return 20;
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const [trashConfirm, setTrashConfirm] = useState(null);
  const [trashSaving, setTrashSaving] = useState(false);
  const showPlayerLinks = type !== 'personal';
  const columnsCount = showPlayerLinks ? 5 : 4;
  const canEditGame = useCallback((game) => game?.type === 'group' && ['active', 'planned'].includes(game?.status), []);
  const getLocalizedGamePath = useCallback((gameId) => getLocalizedPath(`/game/${gameId}`), [getLocalizedPath]);
  const handleGameLinkClick = useCallback(
    (gameId) => {
      const rawPath = `/game/${gameId}`;
      const localizedPath = getLocalizedGamePath(gameId);
      console.debug('[GAME-NAV]', { rawPath, localizedPath });
    },
    [getLocalizedGamePath],
  );
  const getLocalizedInvitePath = useCallback(
    (game, playerId) => {
      const rawPath = buildPlayerInvitePath(game, playerId);
      if (!rawPath) {
        return '';
      }
      return getLocalizedPath(rawPath);
    },
    [getLocalizedPath],
  );
  const playerNameLabel = useCallback(
    (player, index) => {
      if (player.name && player.name.trim()) {
        return player.name;
      }
      if (player.nameMode === 'playerInput') {
        return t('list.player.playerInput');
      }
      return t('list.player.defaultName', { index: index + 1 });
    },
    [t],
  );
  const typeLabel = t(`list.page.typeLabels.${type}`, { defaultValue: '' });
  const title = useMemo(() => {
    if (typeLabel) {
      return t('list.page.title', { type: typeLabel });
    }
    return t('list.page.fallbackTitle');
  }, [t, typeLabel]);
  const dateLocale = useMemo(() => {
    if (i18n.language === 'ru') {
      return 'ru-RU';
    }
    if (i18n.language === 'en') {
      return 'en-US';
    }
    return 'uk-UA';
  }, [i18n.language]);

  const updateQueryParams = useCallback((nextStatus, nextPage, nextPageSize) => {
    setSearchParams({
      tab: nextStatus === 'planned' ? 'future' : nextStatus,
      page: String(nextPage),
      pageSize: String(nextPageSize),
    });
  }, [setSearchParams]);

  const fetchGames = useCallback(async () => {
    if (!token) {
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ type, status, page: String(page), pageSize: String(pageSize) });
      const requestUrl = apiUrl(`/api/games?${params.toString()}`);
      const response = await fetch(requestUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        cache: 'no-store',
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (_error) {
          errorData = {};
        }
        console.error('[GamesList] Failed to load games list', {
          status: response.status,
          statusText: response.statusText,
          url: requestUrl,
          bodyError: errorData?.error || null,
        });
        throw new Error(t('list.errors.load'));
      }
      const data = await response.json();
      setGames(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
      setTotalPages(Number(data.totalPages) || 0);
      setPage(Number(data.page) || page);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      console.error('[GamesList] Request error while loading games list', loadError);
      setError(t('list.errors.load'));
      setGames([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, status, t, token, type]);

  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    const nextStatus = tab === 'future' ? 'planned' : tab;
    const nextPage = Number.parseInt(searchParams.get('page') || '1', 10);
    const nextPageSize = Number.parseInt(searchParams.get('pageSize') || '20', 10);

    if (nextStatus === 'active' || nextStatus === 'planned' || nextStatus === 'finished') {
      setStatus((prev) => (prev === nextStatus ? prev : nextStatus));
    }

    if (Number.isInteger(nextPage) && nextPage > 0) {
      setPage((prev) => (prev === nextPage ? prev : nextPage));
    }

    if ([20, 50, 100].includes(nextPageSize)) {
      setPageSize((prev) => (prev === nextPageSize ? prev : nextPageSize));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const handleTrashAction = (gameId) => {
    setTrashConfirm({ gameId, isFinishedTab: status === 'finished' });
  };

  const handleTrashConfirm = async () => {
    if (!trashConfirm) {
      return;
    }
    const { gameId, isFinishedTab } = trashConfirm;
    setTrashSaving(true);
    try {
      const endpoint = isFinishedTab ? `/api/games/${gameId}/hide` : `/api/games/${gameId}/session/finish`;
      const response = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || (isFinishedTab ? t('list.errors.hide') : t('list.errors.finish')));
      }
      fetchGames();
      setTrashConfirm(null);
    } catch (deleteError) {
      setError(deleteError.message || (isFinishedTab ? t('list.errors.hide') : t('list.errors.finish')));
      setTrashConfirm(null);
    } finally {
      setTrashSaving(false);
    }
  };

  const handlePrevPage = () => {
    if (page <= 1 || loading) {
      return;
    }
    const nextPage = page - 1;
    setPage(nextPage);
    updateQueryParams(status, nextPage, pageSize);
  };

  const handleNextPage = () => {
    if (loading || totalPages === 0 || page >= totalPages) {
      return;
    }
    const nextPage = page + 1;
    setPage(nextPage);
    updateQueryParams(status, nextPage, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const nextPageSize = Number.parseInt(event.target.value, 10);
    if (![20, 50, 100].includes(nextPageSize)) {
      return;
    }
    setPageSize(nextPageSize);
    setPage(1);
    updateQueryParams(status, 1, nextPageSize);
  };

  const handleCopyPlayerLink = async (game, player) => {
    const path = getLocalizedInvitePath(game, player.id);
    if (!path) {
      setError(t('list.errors.linkUnavailable'));
      return;
    }
    const link = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch (_error) {
      window.prompt(t('list.actions.copyManually'), link);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{t('list.page.caption')}</p>
          <h1>{title}</h1>
          <p className="page-description">{t('list.page.description')}</p>
        </div>
      </div>
      <div className="status-tabs">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={status === tab.value ? 'type-tab active' : 'type-tab'}
            onClick={() => {
              setStatus(tab.value);
              setPage(1);
              updateQueryParams(tab.value, 1, pageSize);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {error && (
        <div className="transactions-state transactions-state--error">
          <p className="form-error">{error}</p>
          <button type="button" className="secondary-button" onClick={fetchGames} disabled={loading}>
            {t('list.retry')}
          </button>
        </div>
      )}
      {loading ? (
        <p>{t('list.table.loading')}</p>
      ) : (
        <>
          <div className="games-table-wrapper games-table-wrapper--responsive">
            <table className="games-table">
              <thead>
                <tr>
                  <th>{t('list.table.headers.createdAt')}</th>
                  <th>{t('list.table.headers.title')}</th>
                  <th>{type === 'group' ? t('list.table.headers.playerNames') : t('list.table.headers.playerName')}</th>
                  {showPlayerLinks && <th>{t('list.table.headers.playerLink')}</th>}
                  <th>{t('list.table.headers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {games.length === 0 && (
                  <tr>
                    <td colSpan={columnsCount} className="empty-cell">
                      {t('list.table.empty')}
                    </td>
                  </tr>
                )}
                {games.map((game) => (
                  <tr key={game.id}>
                    <td>{new Date(game.createdAt).toLocaleDateString(dateLocale)}</td>
                    <td>
                      <Link
                        to={getLocalizedGamePath(game.id)}
                        className="table-link"
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => handleGameLinkClick(game.id)}
                      >
                        {game.title}
                      </Link>
                    </td>
                    <td>{game.players.map((player, index) => playerNameLabel(player, index)).join(', ')}</td>
                    {showPlayerLinks && (
                      <td>
                        <div className="player-links">
                          {game.players.map((player) => (
                            <GameActionTooltip key={`player-link-${game.id}-${player.id}`}
                              label={t('list.actions.copyFor', { name: playerNameLabel(player, player.turnOrder || 0) })}>
                            <button
                              type="button"
                              className="icon-button games-list__icon-action"
                              aria-label={t('list.actions.copyFor', { name: playerNameLabel(player, player.turnOrder || 0) })}
                              onClick={() => handleCopyPlayerLink(game, player)}
                            >
                              <span aria-hidden="true">🔗</span>
                            </button>
                            </GameActionTooltip>
                          ))}
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="action-buttons">
                        {canEditGame(game) && (
                          <GameActionTooltip label={t('list.actions.edit')}>
                            <Link className="ghost-button games-list__icon-action"
                              to={getLocalizedPath(`/dashboard/games/group/${game.id}/edit?from=${status === 'planned' ? 'future' : 'current'}`)}
                              aria-label={t('list.actions.edit')}>
                              <span aria-hidden="true">✏️</span>
                            </Link>
                          </GameActionTooltip>
                        )}
                        {(type === 'group' || type === 'individual') && status !== 'planned' && game.hasRecordings && (
                          <GameActionTooltip label={recordingsLabel}>
                            <Link className="ghost-button games-list__icon-action"
                              to={getLocalizedPath('/dashboard/games/' + type + '/' + game.id + '/recordings')}
                              aria-label={recordingsLabel}>
                              <span aria-hidden="true">▶</span>
                            </Link>
                          </GameActionTooltip>
                        )}
                        <GameActionTooltip label={trashLabel}>
                          <button type="button" className="ghost-button games-list__icon-action"
                            aria-label={trashLabel} onClick={() => handleTrashAction(game.id)}>
                            <span aria-hidden="true">🗑️</span>
                          </button>
                        </GameActionTooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="games-cards">
            {games.length === 0 ? (
              <div className="games-card">
                <div className="games-card__row">
                  <span className="games-card__label">{t('list.table.empty')}</span>
                </div>
              </div>
            ) : (
              games.map((game) => (
                <div key={`card-${game.id}`} className="games-card">
                  <div className="games-card__row">
                    <span className="games-card__label">{t('list.table.headers.createdAt')}</span>
                    <span>{new Date(game.createdAt).toLocaleDateString(dateLocale)}</span>
                  </div>
                  <div className="games-card__row">
                    <span className="games-card__label">{t('list.table.headers.title')}</span>
                    <Link
                      to={getLocalizedGamePath(game.id)}
                      className="table-link"
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => handleGameLinkClick(game.id)}
                    >
                      {game.title}
                    </Link>
                  </div>
                  <div className="games-card__row">
                    <span className="games-card__label">
                      {type === 'group' ? t('list.table.headers.playerNames') : t('list.table.headers.playerName')}
                    </span>
                    <span>{game.players.map((player, index) => playerNameLabel(player, index)).join(', ')}</span>
                  </div>
                  {showPlayerLinks && (
                    <div className="games-card__row">
                      <span className="games-card__label">{t('list.table.headers.playerLink')}</span>
                      <div className="player-links">
                        {game.players.map((player) => (
                          <GameActionTooltip key={`player-link-mobile-${game.id}-${player.id}`}
                            label={t('list.actions.copyFor', { name: playerNameLabel(player, player.turnOrder || 0) })}>
                          <button
                            type="button"
                            className="icon-button games-list__icon-action"
                            aria-label={t('list.actions.copyFor', { name: playerNameLabel(player, player.turnOrder || 0) })}
                            onClick={() => handleCopyPlayerLink(game, player)}
                          >
                            <span aria-hidden="true">🔗</span>
                          </button>
                          </GameActionTooltip>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="games-card__actions">
                    {canEditGame(game) && (
                      <GameActionTooltip label={t('list.actions.edit')}>
                        <Link className="ghost-button games-list__icon-action"
                          to={getLocalizedPath(`/dashboard/games/group/${game.id}/edit?from=${status === 'planned' ? 'future' : 'current'}`)}
                          aria-label={t('list.actions.edit')}>
                          <span aria-hidden="true">✏️</span>
                        </Link>
                      </GameActionTooltip>
                    )}
                    {(type === 'group' || type === 'individual') && status !== 'planned' && game.hasRecordings && (
                      <GameActionTooltip label={recordingsLabel}>
                        <Link className="ghost-button games-list__icon-action"
                          to={getLocalizedPath('/dashboard/games/' + type + '/' + game.id + '/recordings')}
                          aria-label={recordingsLabel}>
                          <span aria-hidden="true">▶</span>
                        </Link>
                      </GameActionTooltip>
                    )}
                    <GameActionTooltip label={trashLabel}>
                      <button type="button" className="ghost-button games-list__icon-action"
                        aria-label={trashLabel} onClick={() => handleTrashAction(game.id)}>
                        <span aria-hidden="true">🗑️</span>
                      </button>
                    </GameActionTooltip>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="transactions-pagination">
            <button
              type="button"
              className="secondary-button"
              onClick={handlePrevPage}
              disabled={loading || page <= 1}
            >
              {t('list.pagination.prev')}
            </button>
            <span className="transactions-pagination__info">
              {t('list.pagination.info', {
                page,
                totalPages: Math.max(totalPages, 1),
                total,
              })}
            </span>
            <button
              type="button"
              className="secondary-button"
              onClick={handleNextPage}
              disabled={loading || totalPages === 0 || page >= totalPages}
            >
              {t('list.pagination.next')}
            </button>
            <label className="games-pagination-size" htmlFor="games-page-size">
              {t('list.pagination.pageSize')}
              <select id="games-page-size" value={pageSize} onChange={handlePageSizeChange} disabled={loading}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
        </>
      )}
      {trashConfirm && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal confirm-modal">
            <p className="modal-description">
              {trashConfirm.isFinishedTab
                ? t('list.confirmModal.hide.description')
                : t('list.confirmModal.finish.description')}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => setTrashConfirm(null)}
                disabled={trashSaving}
              >
                {t('list.confirmModal.actions.cancel')}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleTrashConfirm}
                disabled={trashSaving}
              >
                {t('list.confirmModal.actions.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesListPage;
